/**
 * @format
 * @flow strict-local
 */
// DEPENDENCIES

import moment from "moment";
import React, {
	useEffect,
	useRef,
	useState
} from "react";
import {
	useLocation,
	useNavigate,
	useParams
} from "react-router-dom";

import {
	ComponentWrapper,
	MessageListInnerWrapper,

	MessageListItem,
	MessageListItemContent,
	MessageListItemUnreadWrapper,
	MessageListItemWrapper,

	MessageListInnerFooterWrapper,
	MessageListWrapper,

	MessageWrapper
} from "./Messages.styled";

import Button from "../../components/Button";
import Card, {
	CardBody,
	CardFooter,
	CardHeader
} from "../../components/Card";
import Icon from "../../components/Icon";
import Img from "../../components/Img"; 
import LoaderAnimation from "../../components/LoaderAnimation";

import i18nHelper from "../../helpers/i18n";
import routerHelper from "../../helpers/router";
import storeHelper from "../../helpers/store";
import utilsHelper from "../../helpers/utils";

import useApi from "../../hooks/api";
import useDimensions from "../../hooks/dimensions";

import i18n from "../../i18n";

// COMPONENT

const Messages = (props) => {
	const componentWrapperRef = useRef();

	const [
		error, 
		setError
	] = useState(null);
	const [
		isRenderReady, 
		setIsRenderReady
	] = useState(false);
	const [
		message, 
		setMessage
	] = useState(null);
	const [
		messageList, 
		setMessageList
	] = useState([]);
	const [
		messageListPage, 
		setMessageListPage
	] = useState(1);

	const dimensions = useDimensions(componentWrapperRef);
	const {
		fetchApi
	} = useApi();
	const location = useLocation();
	const navigate = useNavigate();
	const params = useParams();

	// Lifecycle

	// Note - Run on bootstrap
	useEffect(
		() => {
			const waitAsync = async () => {
				const response = await fetchApi({
					method: "get",
					params: {
						page: 1,
						service: "getMessages"
					}
				});

				if(response.error) {
					parseApiError(
						response.error.code,
						response.error.message
					);
					
					return;
				}

				const newMessages = response.payload.messages.map((item) => {
					const offset = moment.parseZone(item.date).utcOffset();
					const date = moment.utc(item.date).utcOffset(offset);

					return {
						date: date.format("MMM Do YYYY, h:mm A"),
						id: item.id,
						title: item.title,
						unread: item.unread
					};
				});

				setMessageList(newMessages);

				if(response.payload.next_page) {
					setMessageListPage(response.payload.next_page);
				}
			};

			waitAsync();
	    }, 
		[]
	);

	// Note - Run when messageList change
	useEffect(
		() => {
			if(!utilsHelper.isArray(messageList)) {
				return;
			}

			setIsRenderReady(true);
		}, 
		[messageList]
	);

	// Note - Run when params.messageId change
	useEffect(
		() => {
			if(!params.messageId) {
				return;
			}

			loadMessage(params.messageId);
		}, 
		[params.messageId]
	);

	// Events

	// miss load more

	/**
	 * goBack - ...
	 * 
	 * @param {object} e ... 
	 */
	const goBack = (e) => {
		navigate(-1);
	};

	// Misc

	/**
	 * goToExternal - ...
	 * 
	 * @param {string} url ... 
	 */
	const goToExternal = (url) => {
		if(!url) {
			return;
		}

		window.location.href = url;
	};

	/**
	 * goToMessage - ...
	 * 
	 * @param {string} messageId ... 
	 */
	const goToMessage = (messageId) => {
		if(!messageId) {
			return;
		}

		navigate({
			pathname: routerHelper.getUrl(
				"messages",
				{
					messageId: messageId
				}
			)
		});
	};

	/**
	 * goToReservation - ...
	 * 
	 * @param {string} reservationId ... 
	 */
	const goToReservation = (reservationId) => {
		if(!reservationId) {
			return;
		}

		navigate({
			pathname: routerHelper.getUrl(
				"reservation",
				{
					eventId: reservationId
				}
			)
		});
	};

	/**
	 * loadMessage - ...
	 * 
	 * @param {string} messageId ... 
	 */
	const loadMessage = async (messageId) => {
		const response = await fetchApi({
			method: "get",
			params: {
				message_id: messageId,
				service: "getMessage"
			}
		});

		if(response.error) {
			parseApiError(
				response.error.code,
				response.error.message
			);

			return;
		}

		const {
			event,
			message
		} = response.payload;

		const offset = moment.parseZone(message.date).utcOffset();
		const date = moment.utc(message.date).utcOffset(offset);

		let newMessage = {
			date: date.format("MMM Do YYYY, h:mm A"),
			id: message.id,
			image: message.image,
			text: message.text.replace(/<\/p><p>/g, "\n\n").replace(/<p>/g, "").replace(/<\/p>/g, "").replace(/<br>/g, "\n"),
			title: message.title
		};

		if(
			message.event_status && 
			message.event_status !== "pending"
		) {
			newMessage.button = {
				type: message.event_status
			};
		} else {
			newMessage.button = {
				label: message.link.button_label || "",
				type: message.link.type,
				url: message.link.url
			};
		}

		setMessage(newMessage);
	};

	/**
	 * parseApiError - ...
	 * 
	 * @param {string} code ... 
	 */
	const parseApiError = (code, message) => {
		switch(code) {
			case 102: // Invalid access token
				reconnect();
			return;
			default:
				//setError(code);
				setError(
					message ||
					code
				);
			return;
		}
	};

	/**
	 * reconnect - ...
	 */
	const reconnect = () => {
		const {
			pathname,
			search
		} = location;
	
		navigate({
			pathname: routerHelper.getUrl("signIn"),
			search: `?redirect=${encodeURIComponent(pathname + search)}`
		});
	};

	// Render

	/**
	 * render - ... !!! to complete
	 */
	const render = () => {
		let errorContent;
		let messageContent;
		let messageListContent;

		if(error) {
			errorContent = (
				<div
					style={{
						borderRadius: "8px",
						backgroundColor: "#F04124",
						color: "#fff",
						padding: "16px"
					}}
					>
					{error}
				</div>
			);
		}

		if(isRenderReady) {
			if(dimensions?.width < 768) {
				if(params.messageId) {
					messageContent = renderMessage();
				} else {
					messageListContent = renderMessageList();
				}
			} else {
				messageContent = renderMessage();
				messageListContent = renderMessageList();
			}
		} else {
			// loader
		}

		return (
			<ComponentWrapper
				ref={componentWrapperRef}
				>
				{errorContent}
				<>
				{messageListContent}
				{messageContent}
				</>
			</ComponentWrapper>
		);
	};

	/**
	 * renderMessage - ...
	 */
	const renderMessage = () => {
		if(utilsHelper.isObjectEmpty(message)) {
			return;
		}

		let buttonProps = {
			label: message.button.label
		};

		switch(message.button.type) {
			case "confirmed":
			case "expired":
			case "refused":
				buttonProps.disabled = true;
				buttonProps.label = i18n.t(`messageButtonLabels.${message.button.type}`);
				buttonProps.paletteColor = "disabled";
			break;
			default:
				buttonProps.label = 
					message.button.label || 
					i18n.t("messageButtonLabels.default")
				;
				buttonProps.paletteColor = "dark";

				if(
					message.button.type === "external" && 
					message.button.url
				) {
					buttonProps.onClick = (e) => goToExternal(message.button.url);
				}

				if(message.button.type === "event") {
					buttonProps.onClick = (e) => goToReservation(message.button.url);
				}
			break;
		}

		//if(dimensions?.width < 768) {
		/*
		<a
					onClick={goBack}
					>
					Go Back
				</a>
		*/
		return (
			<MessageWrapper>
			<Card>
				<CardHeader
					className="messageCardHeader"
					>
					<Img
						alt={message.title}
						className="img"
						src={message.image}
					/>
				</CardHeader>
				<CardBody
					className="messageCardBody"
					>
					<small>
						{message.date}
					</small>
					<strong>
						{message.title}
					</strong>
					<p>
						{message.text}
					</p>
				</CardBody>
				<CardFooter
					className="messageCardFooter"
					>
					<Button
						className="button"
						{...buttonProps}
					/>
				</CardFooter>
			</Card>
			</MessageWrapper>
		);
	};

	/**
	 * renderMessageList - ... !!! miss indicator & load more... 
	 */
	const renderMessageList = () => {
		const content = messageList.map((item) => {
			let unreadIndicatorContent;

			if(item.unread) {
				unreadIndicatorContent = (
					<MessageListItemUnreadWrapper>

					</MessageListItemUnreadWrapper>
				);
			}
	
			return (
				<MessageListItem
					key={item.id}
					onClick={(e) => goToMessage(item.id)}
					>
					<MessageListItemContent>
						<small>
							{item.date}
						</small>
						<strong>
							{item.title}
						</strong>
					</MessageListItemContent>
					{unreadIndicatorContent}
					<Icon
						name="chevronRight"
						noBoundingBox
					/>
				</MessageListItem>
			);
		});

		return (
			<MessageListWrapper>
				<MessageListInnerWrapper>
					<MessageListItemWrapper>
						{content}
					</MessageListItemWrapper>
					<MessageListInnerFooterWrapper
						style={{
							display: "none"
						}}
						>
						load more...
					</MessageListInnerFooterWrapper>
				</MessageListInnerWrapper>
			</MessageListWrapper>
		);
	};

	return render();
};

// EXPORT

export default Messages;
